import React from 'react';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { useLazyNcziProductListQuery } from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import DynamicSelect from '../../../../Components/Base/DynamicSelect/DynamicSelect';
import * as yup from 'yup';
import { FieldError } from 'react-hook-form';

export interface NCZIProductSelectItem {
	label: string;
	value: {
		code: string;
		name: string;
		additionalName: string;
		opl: boolean;
		antibioticsChemotherapy: boolean;
		tailored: boolean;
		type: string;
		atcCode: string | null;
		atcName: string | null;
		atcAddon: string | null;
		ioNeeded: boolean;
		ioOptions: string[];
		ventilator: boolean;
		quantityLimit: string[];
		pilList?: string[];
	};
}

export const NCZIProductValidator = (name: string) =>
	yup.object({
		label: yup.string(),
		value: yup.object({
			code: yup.string().required(name),
			name: yup.string(),
			additionalName: yup.string(),
			opl: yup.boolean(),
			antibioticsChemotherapy: yup.boolean(),
			tailored: yup.boolean(),
			type: yup.string(),
			atcCode: yup.string().nullable(),
			ioNeeded: yup.boolean(),
			ioOptions: yup.array().of(yup.string()),
			ventilator: yup.boolean(),
			quantityLimit: yup.array().of(yup.string()),
		}),
	});

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
	portal?: boolean;
}

const NCZIProductSearchSelect = React.forwardRef<
	any,
	React.PropsWithChildren<Props>
>((props, ref) => {
	const { value, onChange } = props;
	const [getListProductItems] = useLazyNcziProductListQuery();

	useEffectAsync(async () => {
		if (value) onChange?.(value);
	});

	const handleSearchItem = async (
		searchInput: string,
	): Promise<NCZIProductSelectItem[]> => {
		const { items: listProductItems } = await getListProductItems({
			fulltext: searchInput,
		}).unwrap();

		const items: NCZIProductSelectItem[] = [];
		for (const productItem of listProductItems?.data || []) {
			if (productItem && productItem.code) {
				const ioOptions: string[] =
					productItem.ioOptions?.filter<string>(
						(ioi): ioi is string => ioi !== null,
					) || [];
				const quantityLimit: string[] =
					productItem.quantityLimit?.filter<string>(
						(qli): qli is string => qli !== null,
					) || [];

				items.push({
					label:
						productItem.code +
						': ' +
						productItem.name +
						' - ' +
						productItem.additionalName,
					value: {
						code: productItem.code,
						name: productItem.name,
						additionalName: productItem.additionalName || '',
						opl: productItem.opl || false,
						antibioticsChemotherapy:
							productItem.antibioticsChemotherapy || false,
						tailored: productItem.tailored || false,
						type: productItem.type,
						atcCode: productItem.atcCode || null,
						atcName: productItem.atcName || null,
						atcAddon: productItem.atcAddon || null,
						ioNeeded: productItem.ioNeeded || false,
						ioOptions,
						ventilator: productItem.ventilator || false,
						quantityLimit,
						pilList: productItem.pilList || undefined,
					},
				});
			}
		}

		return items;
	};

	return <DynamicSelect searchItems={handleSearchItem} {...props} />;
});

export default NCZIProductSearchSelect;
